/**
 * Export Dialog Component for Reels video editor
 * Export settings, format selection, and progress
 */

import React, { useState, useCallback, useRef } from 'react';
import { X, Download, Play, Settings, Check, AlertCircle, Loader2, Instagram, Youtube, Facebook, Twitter } from 'lucide-react';
import { useProject, getProjectDuration } from '../stores/projectStore';
import { EXPORT_PRESETS } from '../utils/effectPresets';
import { formatDuration } from '../utils/timeUtils';

const ExportDialog = ({ isOpen, onClose }) => {
  const { project } = useProject();
  const [selectedPreset, setSelectedPreset] = useState('instagram-reels');
  const [customSettings, setCustomSettings] = useState({
    width: 1080,
    height: 1920,
    fps: 30,
    bitrate: 8000000,
    format: 'mp4',
  });
  const [useCustom, setUseCustom] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [exportStatus, setExportStatus] = useState('idle'); // idle, exporting, success, error
  const [exportError, setExportError] = useState('');
  const [exportedBlob, setExportedBlob] = useState(null);

  const canvasRef = useRef(null);
  const videoRef = useRef(null);
  const loadedImagesRef = useRef({});
  const exportCancelledRef = useRef(false);

  const duration = getProjectDuration(project);
  const preset = EXPORT_PRESETS[selectedPreset];

  const getPlatformIcon = (presetId) => {
    if (presetId.includes('instagram')) return Instagram;
    if (presetId.includes('youtube')) return Youtube;
    if (presetId.includes('facebook')) return Facebook;
    if (presetId.includes('twitter')) return Twitter;
    return Settings;
  };

  // Get media by ID
  const getMediaById = useCallback((mediaId) => {
    return project.mediaLibrary.find(m => m.id === mediaId);
  }, [project.mediaLibrary]);

  // Check if media is an image
  const isImageMedia = useCallback((media) => {
    if (!media) return false;
    return media.type === 'image' ||
           (media.mimeType && media.mimeType.startsWith('image/')) ||
           /\.(png|jpg|jpeg|gif|webp|bmp|svg)$/i.test(media.name || '');
  }, []);

  // Get clip at time
  const getClipAtTime = useCallback((time) => {
    for (const track of project.tracks) {
      if (track.type !== 'video' || !track.visible) continue;
      for (const clip of track.clips) {
        if (time >= clip.startTime && time < clip.startTime + clip.duration) {
          return clip;
        }
      }
    }
    return null;
  }, [project.tracks]);

  // Load image helper
  const loadImage = useCallback((url, mediaId) => {
    return new Promise((resolve, reject) => {
      if (loadedImagesRef.current[mediaId]) {
        resolve(loadedImagesRef.current[mediaId]);
        return;
      }
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        loadedImagesRef.current[mediaId] = img;
        resolve(img);
      };
      img.onerror = reject;
      img.src = url;
    });
  }, []);

  // Render single frame
  const renderFrameAtTime = useCallback(async (ctx, canvas, time) => {
    const exportSettings = useCustom ? customSettings : preset;
    canvas.width = exportSettings.width;
    canvas.height = exportSettings.height;

    // Clear canvas with black
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const clip = getClipAtTime(time);
    if (!clip) return;

    const media = getMediaById(clip.mediaId);
    if (!media) return;

    const isImage = isImageMedia(media);

    // Apply effects/filters if any
    if (clip.effects && clip.effects.length > 0) {
      const filters = clip.effects
        .filter(e => e.type === 'filter' && e.filter)
        .map(e => e.filter)
        .join(' ');
      ctx.filter = filters || 'none';
    }

    if (isImage) {
      // Render image
      try {
        const img = await loadImage(media.url, media.id);
        const imageAspect = img.naturalWidth / img.naturalHeight;
        const canvasAspect = canvas.width / canvas.height;

        let sx = 0, sy = 0, sw = img.naturalWidth, sh = img.naturalHeight;

        if (imageAspect > canvasAspect) {
          sw = img.naturalHeight * canvasAspect;
          sx = (img.naturalWidth - sw) / 2;
        } else {
          sh = img.naturalWidth / canvasAspect;
          sy = (img.naturalHeight - sh) / 2;
        }

        ctx.drawImage(img, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
      } catch (e) {
        console.error('Error loading image for export:', e);
      }
    } else {
      // For video, we'd need to seek the video element - this requires a different approach
      // For now, we'll indicate that video export requires backend processing
      const video = videoRef.current;
      if (video && video.src && media.url) {
        // Set video source if changed
        if (video.src !== media.url) {
          video.src = media.url;
          await new Promise(resolve => {
            video.onloadeddata = resolve;
            video.load();
          });
        }

        // Seek to specific time
        const clipTime = time - clip.startTime + (clip.inPoint || 0);
        video.currentTime = Math.max(0, Math.min(clipTime, video.duration || 0));

        // Wait for seek to complete
        await new Promise(resolve => {
          video.onseeked = resolve;
          setTimeout(resolve, 100); // Fallback timeout
        });

        if (video.readyState >= 2) {
          const videoAspect = video.videoWidth / video.videoHeight;
          const canvasAspect = canvas.width / canvas.height;

          let sx = 0, sy = 0, sw = video.videoWidth, sh = video.videoHeight;

          if (videoAspect > canvasAspect) {
            sw = video.videoHeight * canvasAspect;
            sx = (video.videoWidth - sw) / 2;
          } else {
            sh = video.videoWidth / canvasAspect;
            sy = (video.videoHeight - sh) / 2;
          }

          ctx.drawImage(video, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
        }
      }
    }

    ctx.filter = 'none';

    // Render text overlays
    project.textClips.forEach(textClip => {
      if (time >= textClip.startTime && time < textClip.startTime + textClip.duration) {
        const x = (textClip.position?.x || 0.5) * canvas.width;
        const y = (textClip.position?.y || 0.5) * canvas.height;

        ctx.save();
        ctx.translate(x, y);

        if (textClip.rotation) {
          ctx.rotate((textClip.rotation * Math.PI) / 180);
        }

        const fontSize = (textClip.fontSize || 48) * (canvas.width / 1080);
        ctx.font = `${textClip.fontWeight || 'bold'} ${fontSize}px ${textClip.fontFamily || 'Arial'}`;
        ctx.textAlign = textClip.textAlign || 'center';
        ctx.textBaseline = 'middle';

        if (textClip.strokeColor && textClip.strokeWidth) {
          ctx.strokeStyle = textClip.strokeColor;
          ctx.lineWidth = textClip.strokeWidth * 2;
          ctx.lineJoin = 'round';
          ctx.strokeText(textClip.text || '', 0, 0);
        }

        ctx.fillStyle = textClip.color || '#ffffff';
        ctx.fillText(textClip.text || '', 0, 0);

        ctx.restore();
      }
    });
  }, [project, useCustom, customSettings, preset, getClipAtTime, getMediaById, isImageMedia, loadImage]);

  // Start export
  const handleExport = useCallback(async () => {
    setIsExporting(true);
    setExportStatus('exporting');
    setExportProgress(0);
    setExportError('');
    setExportedBlob(null);
    exportCancelledRef.current = false;

    try {
      const exportSettings = useCustom ? customSettings : preset;
      const fps = exportSettings.fps || 30;
      const totalFrames = Math.ceil(duration * fps);
      const frameInterval = 1000 / fps;

      // Create offscreen canvas
      const canvas = document.createElement('canvas');
      canvas.width = exportSettings.width;
      canvas.height = exportSettings.height;
      const ctx = canvas.getContext('2d');

      // Setup MediaRecorder
      const stream = canvas.captureStream(fps);
      const mimeType = exportSettings.format === 'webm' ? 'video/webm;codecs=vp9' : 'video/webm;codecs=vp8';

      if (!MediaRecorder.isTypeSupported(mimeType)) {
        throw new Error(`Video format not supported by your browser. Please try WebM format.`);
      }

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType,
        videoBitsPerSecond: exportSettings.bitrate || 8000000,
      });

      const chunks = [];
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.push(e.data);
        }
      };

      // Start recording
      mediaRecorder.start(100);

      // Render frames
      for (let frame = 0; frame < totalFrames; frame++) {
        if (exportCancelledRef.current) {
          mediaRecorder.stop();
          throw new Error('Export cancelled');
        }

        const currentTime = frame / fps;
        await renderFrameAtTime(ctx, canvas, currentTime);

        // Update progress
        setExportProgress(Math.round((frame / totalFrames) * 100));

        // Allow UI to update
        await new Promise(resolve => setTimeout(resolve, 10));
      }

      // Stop recording and wait for data
      const blob = await new Promise((resolve, reject) => {
        mediaRecorder.onstop = () => {
          const finalBlob = new Blob(chunks, { type: mimeType });
          resolve(finalBlob);
        };
        mediaRecorder.onerror = reject;
        mediaRecorder.stop();
      });

      setExportedBlob(blob);
      setExportStatus('success');
    } catch (error) {
      console.error('Export error:', error);
      setExportStatus('error');
      setExportError(error.message || 'Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  }, [duration, useCustom, customSettings, preset, renderFrameAtTime]);

  // Download the exported video
  const handleDownload = useCallback(() => {
    if (!exportedBlob) {
      setExportError('No video to download');
      return;
    }

    const exportSettings = useCustom ? customSettings : preset;
    const extension = exportSettings.format === 'webm' ? 'webm' : 'webm'; // Browser exports to webm
    const url = URL.createObjectURL(exportedBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${project.name || 'Untitled Reel'}.${extension}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    // Reset state
    setExportStatus('idle');
    setExportedBlob(null);
    onClose();
  }, [exportedBlob, project.name, useCustom, customSettings, preset, onClose]);

  // Cancel export
  const handleCancelExport = useCallback(() => {
    exportCancelledRef.current = true;
    setExportStatus('idle');
    setIsExporting(false);
  }, []);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      {/* Dialog */}
      <div className="relative w-full max-w-2xl bg-slate-900 rounded-xl shadow-2xl border border-slate-800 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800">
          <h2 className="text-lg font-semibold text-white">Export Reel</h2>
          <button
            onClick={onClose}
            disabled={isExporting}
            className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {exportStatus === 'idle' && (
            <>
              {/* Project info */}
              <div className="mb-6 p-4 bg-slate-800/50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-white font-medium">{project.name || 'Untitled Reel'}</div>
                    <div className="text-sm text-slate-400">
                      Duration: {formatDuration(duration)} - {project.settings.width}x{project.settings.height}
                    </div>
                  </div>
                  <Play className="w-8 h-8 text-slate-600" />
                </div>
              </div>

              {/* Preset selection */}
              <div className="mb-6">
                <h3 className="text-sm font-medium text-white mb-3">Export Preset</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {Object.entries(EXPORT_PRESETS).map(([id, p]) => {
                    const Icon = getPlatformIcon(id);
                    return (
                      <button
                        key={id}
                        onClick={() => {
                          setSelectedPreset(id);
                          setUseCustom(false);
                        }}
                        className={`flex items-center gap-2 p-3 rounded-lg border transition-all ${
                          selectedPreset === id && !useCustom
                            ? 'border-teal-500 bg-teal-600/20 text-white'
                            : 'border-slate-700 bg-slate-800 text-slate-400 hover:border-slate-600 hover:text-white'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        <span className="text-sm">{p.name}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Selected preset details */}
              {!useCustom && preset && (
                <div className="mb-6 p-4 bg-slate-800 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-slate-400">Resolution</span>
                    <span className="text-sm text-white">{preset.width} x {preset.height}</span>
                  </div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-slate-400">Frame Rate</span>
                    <span className="text-sm text-white">{preset.fps} FPS</span>
                  </div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-slate-400">Format</span>
                    <span className="text-sm text-white uppercase">{preset.format}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-400">Quality</span>
                    <span className="text-sm text-white">{Math.round(preset.bitrate / 1000000)} Mbps</span>
                  </div>
                </div>
              )}

              {/* Custom settings toggle */}
              <button
                onClick={() => setUseCustom(!useCustom)}
                className="flex items-center gap-2 text-sm text-slate-400 hover:text-white mb-4"
              >
                <Settings className="w-4 h-4" />
                {useCustom ? 'Use preset' : 'Custom settings'}
              </button>

              {/* Custom settings form */}
              {useCustom && (
                <div className="mb-6 p-4 bg-slate-800 rounded-lg space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-slate-400 mb-1 block">Width</label>
                      <input
                        type="number"
                        value={customSettings.width}
                        onChange={(e) => setCustomSettings({ ...customSettings, width: Number(e.target.value) })}
                        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-slate-400 mb-1 block">Height</label>
                      <input
                        type="number"
                        value={customSettings.height}
                        onChange={(e) => setCustomSettings({ ...customSettings, height: Number(e.target.value) })}
                        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-slate-400 mb-1 block">Frame Rate</label>
                      <select
                        value={customSettings.fps}
                        onChange={(e) => setCustomSettings({ ...customSettings, fps: Number(e.target.value) })}
                        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm"
                      >
                        <option value={24}>24 FPS</option>
                        <option value={30}>30 FPS</option>
                        <option value={60}>60 FPS</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-slate-400 mb-1 block">Format</label>
                      <select
                        value={customSettings.format}
                        onChange={(e) => setCustomSettings({ ...customSettings, format: e.target.value })}
                        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm"
                      >
                        <option value="mp4">MP4 (H.264)</option>
                        <option value="webm">WebM (VP9)</option>
                        <option value="gif">GIF</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Hidden video element for video exports */}
          <video
            ref={videoRef}
            className="hidden"
            muted
            playsInline
            crossOrigin="anonymous"
          />

          {/* Exporting progress */}
          {exportStatus === 'exporting' && (
            <div className="py-8 text-center">
              <Loader2 className="w-12 h-12 text-teal-500 animate-spin mx-auto mb-4" />
              <h3 className="text-lg font-medium text-white mb-2">Exporting...</h3>
              <p className="text-slate-400 text-sm mb-6">
                Rendering frames. Please don't close this window.
              </p>

              {/* Progress bar */}
              <div className="w-full max-w-md mx-auto bg-slate-800 rounded-full h-2 mb-2">
                <div
                  className="h-full bg-teal-500 rounded-full transition-all duration-300"
                  style={{ width: `${exportProgress}%` }}
                />
              </div>
              <span className="text-sm text-slate-500">{exportProgress}%</span>

              <button
                onClick={handleCancelExport}
                className="mt-6 px-4 py-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          )}

          {/* Export success */}
          {exportStatus === 'success' && (
            <div className="py-8 text-center">
              <div className="w-16 h-16 rounded-full bg-green-600/20 flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-500" />
              </div>
              <h3 className="text-lg font-medium text-white mb-2">Export Complete!</h3>
              <p className="text-slate-400 text-sm mb-4">
                Your reel is ready to download.
              </p>
              {exportedBlob && (
                <p className="text-slate-500 text-xs mb-6">
                  File size: {(exportedBlob.size / (1024 * 1024)).toFixed(2)} MB (WebM format)
                </p>
              )}

              <div className="flex items-center justify-center gap-3">
                <button
                  onClick={() => {
                    setExportStatus('idle');
                    setExportedBlob(null);
                    setExportProgress(0);
                  }}
                  className="px-4 py-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
                >
                  Export Again
                </button>
                <button
                  onClick={handleDownload}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-teal-600 hover:bg-teal-500 text-white rounded-lg font-medium transition-colors"
                >
                  <Download className="w-5 h-5" />
                  Download Reel
                </button>
              </div>
            </div>
          )}

          {/* Export error */}
          {exportStatus === 'error' && (
            <div className="py-8 text-center">
              <div className="w-16 h-16 rounded-full bg-red-600/20 flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-red-500" />
              </div>
              <h3 className="text-lg font-medium text-white mb-2">Export Failed</h3>
              <p className="text-red-400 text-sm mb-6">
                {exportError}
              </p>

              <div className="flex items-center justify-center gap-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    setExportStatus('idle');
                    setExportProgress(0);
                    setExportError('');
                    setExportedBlob(null);
                  }}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-medium transition-colors"
                >
                  Try Again
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {exportStatus === 'idle' && (
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-800 bg-slate-900/50">
            <button
              onClick={onClose}
              className="px-4 py-2 text-slate-400 hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleExport}
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-teal-600 hover:bg-teal-500 text-white rounded-lg font-medium transition-colors"
            >
              <Download className="w-4 h-4" />
              Export
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExportDialog;
